// In-place text editing. Saves shortly after typing stops, on blur, when the page is hidden or
// unloaded, and on unmount. Local drafts survive re-renders caused by polling; the server value
// only replaces the draft while the field is not being edited. Escape restores the text the field
// had when editing began.
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { onPageHide } from './helpers';

/** Pause in typing after which the draft is saved (a reload or a closed app then loses at most this). */
export const TYPING_SAVE_DELAY_MS = 800;

interface EditableBaseProps {
  value: string;
  /** Called with the new text when it differs from `value` (while typing, on blur, Ctrl/Cmd+Enter or unmount). */
  onSave: (text: string) => void;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Edit session shared by the editors: `read()` returns the current text of the field.
 * begin/end bracket a focus period; `changed()` is called on every keystroke.
 */
function useEditSession(value: string, onSave: (text: string) => void, read: () => string | null) {
  const editing = useRef(false);
  const latest = useRef({ value, onSave, read });
  useLayoutEffect(() => {
    latest.current = { value, onSave, read };
  });
  /** Text handed to onSave last; the reload that shows it as `value` can take a while. */
  const lastSaved = useRef<string | null>(null);
  /** `value` when editing began (Escape returns to it) and whether this edit saved anything yet. */
  const original = useRef(value);
  const savedThisEdit = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopHideFlush = useRef<(() => void) | null>(null);

  useEffect(() => {
    lastSaved.current = null;
  }, [value]);

  const clearTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const save = (text: string) => {
    lastSaved.current = text;
    savedThisEdit.current = true;
    latest.current.onSave(text);
  };

  const commit = () => {
    clearTimer();
    const text = latest.current.read();
    if (text === null || text === lastSaved.current) return;
    const current = latest.current.value;
    if (text !== current && text.trim() !== current.trim()) save(text);
  };

  const begin = () => {
    if (editing.current) return;
    editing.current = true;
    original.current = latest.current.value;
    savedThisEdit.current = false;
    stopHideFlush.current?.();
    stopHideFlush.current = onPageHide(commit);
  };

  const finish = () => {
    editing.current = false;
    stopHideFlush.current?.();
    stopHideFlush.current = null;
  };

  /** Blur: save what was typed. */
  const end = () => {
    if (!editing.current) return;
    finish();
    commit();
  };

  /** Escape: back to the text from before editing. Returns that text. */
  const revert = (): string => {
    clearTimer();
    finish();
    const back = original.current;
    // Typing already saved part of the edit: store the original text again.
    if (savedThisEdit.current && back !== lastSaved.current) save(back);
    return back;
  };

  const changed = () => {
    if (!editing.current) return;
    clearTimer();
    timer.current = setTimeout(commit, TYPING_SAVE_DELAY_MS);
  };

  // Unsaved text is saved when the component goes away while focused (e.g. navigation).
  // These helpers only read refs, so the first render's closures are always current.
  useEffect(
    () => () => {
      if (editing.current) {
        finish();
        commit();
      }
      clearTimer();
    },
    [],
  );

  return { editing, begin, end, revert, changed };
}

/** Textarea draft state on top of useEditSession. */
function useTextDraft(value: string, onSave: (text: string) => void) {
  const [draft, setDraftState] = useState(value);
  const draftRef = useRef(draft);
  const session = useEditSession(value, onSave, () => draftRef.current);

  useEffect(() => {
    if (!session.editing.current) {
      draftRef.current = value;
      setDraftState(value);
    }
  }, [value, session.editing]);

  const setDraft = (text: string) => {
    draftRef.current = text;
    setDraftState(text);
    session.changed();
  };

  const revert = () => {
    const back = session.revert();
    draftRef.current = back;
    setDraftState(back);
  };

  return { draft, setDraft, begin: session.begin, end: session.end, revert };
}

function autosize(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
}

/** Keeps an auto-sized textarea's height right when its width changes (rotation, sidebar). */
function useAutosizeOnResize(ref: RefObject<HTMLTextAreaElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth !== width) {
        width = el.clientWidth;
        autosize(el);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
}

/** Auto-growing textarea for short and medium texts (entries, notes). */
export function EditableText({ value, onSave, ariaLabel, placeholder, className, disabled }: EditableBaseProps) {
  const { draft, setDraft, begin, end, revert } = useTextDraft(value, onSave);
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => autosize(ref.current), [draft]);
  useAutosizeOnResize(ref);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      revert();
      e.currentTarget.blur();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <textarea
      ref={ref}
      className={`editable${className ? ` ${className}` : ''}`}
      value={draft}
      rows={1}
      aria-label={ariaLabel}
      placeholder={placeholder}
      disabled={disabled}
      spellCheck
      lang="sl"
      onFocus={begin}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={end}
      onKeyDown={onKeyDown}
    />
  );
}

let plaintextOnly: boolean | null = null;

/** contenteditable="plaintext-only" keeps pasted formatting out; not every engine supports it. */
function supportsPlaintextOnly(): boolean {
  if (plaintextOnly !== null) return plaintextOnly;
  try {
    const el = document.createElement('div');
    el.contentEditable = 'plaintext-only';
    plaintextOnly = el.contentEditable === 'plaintext-only';
  } catch {
    plaintextOnly = false;
  }
  return plaintextOnly;
}

/** Text of an editable element with line breaks (innerText is not implemented by jsdom). */
function readText(el: HTMLElement): string {
  const text = typeof el.innerText === 'string' ? el.innerText : (el.textContent ?? '');
  return text.replace(/\u00a0/g, ' ').replace(/\n$/, '');
}

interface EditableParagraphProps extends EditableBaseProps {
  onFocus?: () => void;
  /** Rendered as data-segment-id (used to find the playing segment). */
  segmentId?: string;
}

/**
 * Paragraph edited in place (contenteditable). Used for long transcripts: unlike auto-sized
 * textareas it needs no layout measurements, so hundreds of segments render quickly.
 */
export function EditableParagraph({ value, onSave, ariaLabel, className, onFocus, placeholder, segmentId }: EditableParagraphProps) {
  const ref = useRef<HTMLDivElement>(null);
  // React clears `ref` before unmount cleanups run; the unmount save still needs the element.
  const node = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    node.current = ref.current;
  }, []);
  const session = useEditSession(value, onSave, () => {
    const el = ref.current ?? node.current;
    return el ? readText(el) : null;
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !session.editing.current && readText(el) !== value) el.textContent = value;
  }, [value, session.editing]);

  return (
    <div
      ref={ref}
      className={`editable-p${className ? ` ${className}` : ''}`}
      contentEditable={supportsPlaintextOnly() ? 'plaintext-only' : true}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      data-placeholder={placeholder}
      data-segment-id={segmentId}
      spellCheck
      lang="sl"
      onFocus={() => {
        session.begin();
        onFocus?.();
      }}
      onInput={session.changed}
      onBlur={session.end}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.currentTarget.textContent = session.revert();
          e.currentTarget.blur();
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      onPaste={(e) => {
        if (supportsPlaintextOnly()) return;
        // Fallback for engines without plaintext-only: insert pasted content as plain text.
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        try {
          document.execCommand('insertText', false, text);
        } catch {
          e.currentTarget.textContent = `${e.currentTarget.textContent ?? ''}${text}`;
        }
        session.changed();
      }}
    />
  );
}

/**
 * Document title shown as a heading-sized field that wraps onto more lines on narrow screens
 * (a single-line input cut long titles off); Enter finishes editing, line breaks are not kept.
 */
export function TitleInput({ value, onSave, ariaLabel, placeholder }: EditableBaseProps) {
  const { draft, setDraft, begin, end, revert } = useTextDraft(value, onSave);
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => autosize(ref.current), [draft]);
  useAutosizeOnResize(ref);

  return (
    <textarea
      ref={ref}
      className="title-input"
      rows={1}
      value={draft}
      aria-label={ariaLabel}
      placeholder={placeholder}
      maxLength={200}
      enterKeyHint="done"
      spellCheck
      lang="sl"
      onFocus={begin}
      // Pasted line breaks become spaces: a title is one line of text.
      onChange={(e) => setDraft(e.target.value.replace(/[ \t]*[\r\n]+[ \t]*/g, ' '))}
      onBlur={end}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          revert();
          e.currentTarget.blur();
        }
      }}
    />
  );
}
